import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { getErrorMessage } from '../utils/errorHandler';
import { toast } from 'sonner';
import { Plus, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import LogoLoader from '@/components/LogoLoader';

const Clients = () => {
  const { token, user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    billing_address: '',
    currency: 'USD',
    tax_reg_no: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const data = await api.get('/clients', token);
      setClients(data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean up empty strings for optional fields
      const payload = {
        ...formData,
        billing_address: formData.billing_address || null,
        tax_reg_no: formData.tax_reg_no || null,
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null
      };
      
      if (editingClient) {
        // Update existing client
        await api.patch(`/clients/${editingClient.id}`, payload, token);
        toast.success('Client updated successfully');
      } else {
        // Create new client
        await api.post('/clients', payload, token);
        toast.success('Client created successfully');
      }
      
      setShowDialog(false);
      setEditingClient(null);
      loadClients();
      setFormData({
        name: '',
        billing_address: '',
        currency: 'USD',
        tax_reg_no: '',
        contact_email: '',
        contact_phone: ''
      });
    } catch (error) {
      toast.error(editingClient ? 'Failed to update client' : 'Failed to create client');
    }
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      billing_address: client.billing_address || '',
      currency: client.currency,
      tax_reg_no: client.tax_reg_no || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || ''
    });
    setShowDialog(true);
  };

  const handleDialogClose = (open) => {
    setShowDialog(open);
    if (!open) {
      setEditingClient(null);
      setFormData({
        name: '',
        billing_address: '',
        currency: 'USD',
        tax_reg_no: '',
        contact_email: '',
        contact_phone: ''
      });
    }
  };

  const toggleClientStatus = async (clientId, currentStatus) => {
    try {
      await api.patch(`/clients/${clientId}`, { is_active: !currentStatus }, token);
      toast.success(`Client ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadClients();
    } catch (error) {
      console.error('Toggle client status error:', error);
      toast.error(getErrorMessage(error, 'Failed to update client status'));
    }
  };

  if (loading) {
    return <LogoLoader message="Loading clients..." />;
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600 mt-1">Manage your client base</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-utis-forest hover:bg-utis-forest/90 text-white" data-testid="add-client-button">
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {clients.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No clients yet. Click "Add Client" to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Tax Reg No</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Active</TableHead>
                  {(user?.role === 'Admin' || user?.role === 'Operations Manager') && (
                    <TableHead>Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id} className={!client.is_active ? 'opacity-60 bg-gray-50' : ''}>
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>{client.contact_email || '-'}</TableCell>
                    <TableCell>{client.contact_phone || '-'}</TableCell>
                    <TableCell>{client.currency}</TableCell>
                    <TableCell>{client.tax_reg_no || '-'}</TableCell>
                    <TableCell>
                      {client.is_active ? (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={client.is_active}
                        onCheckedChange={() => toggleClientStatus(client.id, client.is_active)}
                        data-testid={`toggle-client-${client.id}`}
                      />
                    </TableCell>
                    {(user?.role === 'Admin' || user?.role === 'Operations Manager') && (
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(client)}
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={handleDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
            <DialogDescription>
              {editingClient ? 'Update client information' : 'Add a new client to your database'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Client Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="ACME Corp"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                placeholder="billing@acme.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.contact_phone}
                onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                placeholder="+1-555-1234"
              />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={formData.currency} onValueChange={(val) => setFormData({...formData, currency: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="INR">INR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tax Registration No</Label>
              <Input
                value={formData.tax_reg_no}
                onChange={(e) => setFormData({...formData, tax_reg_no: e.target.value})}
                placeholder="US-TAX-001"
              />
            </div>
            <div>
              <Label>Billing Address</Label>
              <Input
                value={formData.billing_address}
                onChange={(e) => setFormData({...formData, billing_address: e.target.value})}
                placeholder="123 Business St, City, State ZIP"
              />
            </div>
            <Button type="submit" className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white">
              {editingClient ? 'Update Client' : 'Create Client'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clients;
